import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, Medal, Award, ShieldAlert } from 'lucide-react';
import { API } from '../config';

const playfair = "'Playfair Display', serif";
const fmtIDR = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n || 0);

const FILTERS = [
  { id: 'all',                label: 'Semua' },
  { id: 'top_spender_month',  label: 'Top Spender Bulan Ini' },
  { id: 'top_spender_last',   label: 'Top Spender Bulan Lalu' },
  { id: 'top_spender_all',    label: 'Top Spender All Time' },
  { id: 'highest_coin',       label: 'Saldo Coin Tertinggi' },
  { id: 'most_kuji',          label: 'Paling Banyak Kuji' },
  { id: 'most_orders',        label: 'Paling Banyak Order' },
  { id: 'newest',             label: 'User Terbaru' },
  { id: 'unverified',         label: 'Email Belum Verified' },
];

export const UsersTabWithAnalytics = ({ usersList, setUsersList, setBalanceModalUser, cardStyle, goldBtn, formatCoins }) => {
  const [filter, setFilter] = useState('all');
  const [topSpenders, setTopSpenders] = useState({ month: [], last_month: [], all: [] });

  useEffect(() => {
    (async () => {
      try {
        const [m, l, a] = await Promise.all([
          axios.get(`${API}/admin/analytics/top-spenders?period=month&limit=3`, { withCredentials: true }),
          axios.get(`${API}/admin/analytics/top-spenders?period=last_month&limit=3`, { withCredentials: true }),
          axios.get(`${API}/admin/analytics/top-spenders?period=all&limit=3`, { withCredentials: true }),
        ]);
        setTopSpenders({ month: m.data || [], last_month: l.data || [], all: a.data || [] });
      } catch (e) {
        console.error('top-spenders fetch failed', e);
      }
    })();
  }, []);

  const filteredUsers = useMemo(() => {
    const arr = [...(usersList || [])];
    switch (filter) {
      case 'top_spender_month':  return arr.sort((a, b) => (b.monthly_spending_idr || 0) - (a.monthly_spending_idr || 0));
      case 'top_spender_last':   return arr.sort((a, b) => (b.lifetime_spending_idr || 0) - (a.lifetime_spending_idr || 0)); // approximation
      case 'top_spender_all':    return arr.sort((a, b) => (b.lifetime_spending_idr || 0) - (a.lifetime_spending_idr || 0));
      case 'highest_coin':       return arr.sort((a, b) => (b.coin_balance || 0) - (a.coin_balance || 0));
      case 'most_kuji':          return arr.sort((a, b) => (b.total_kuji_draws || 0) - (a.total_kuji_draws || 0));
      case 'most_orders':        return arr.sort((a, b) => (b.total_orders || 0) - (a.total_orders || 0));
      case 'newest':             return arr; // already created_at DESC from backend
      case 'unverified':         return arr.filter((u) => !u.email_verified);
      default:                   return arr;
    }
  }, [usersList, filter]);

  const renderRankBlock = (label, list) => (
    <div className="rounded-2xl p-4 backdrop-blur-xl" style={cardStyle} data-testid={`rank-block-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <p className="text-xs uppercase tracking-wider mb-3" style={{ color: '#D4AF37' }}>{label}</p>
      {list.length === 0 ? (
        <p className="text-xs italic" style={{ color: '#A3B8AC' }}>Belum ada data.</p>
      ) : (
        <div className="space-y-2">
          {list.map((s, idx) => {
            const Icon = idx === 0 ? Trophy : idx === 1 ? Medal : Award;
            const tone = idx === 0 ? '#D4AF37' : idx === 1 ? '#A8A8A8' : '#CD7F32';
            return (
              <div key={s.user_id} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: 'rgba(0,0,0,0.25)' }} data-testid={`rank-${idx+1}`}>
                <Icon className="w-5 h-5" style={{ color: tone }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate" style={{ color: '#F8FAF9' }}>#{idx + 1} {s.name}</p>
                  <p className="text-[10px] truncate" style={{ color: '#A3B8AC' }}>{s.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold" style={{ color: tone }}>{fmtIDR(s.total_spending_idr)}</p>
                  <p className="text-[10px]" style={{ color: '#A3B8AC' }}>{s.total_orders} order · {s.total_kuji_draws} kuji</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <>
      <h2 className="text-2xl font-light mb-4" style={{ fontFamily: playfair, color: '#F8FAF9' }}>User Management & Analytics</h2>

      {/* Ranking cards */}
      <div className="grid md:grid-cols-3 gap-3 mb-5" data-testid="ranking-cards">
        {renderRankBlock('Top Spender Bulan Ini', topSpenders.month)}
        {renderRankBlock('Top Spender Bulan Lalu', topSpenders.last_month)}
        {renderRankBlock('Top Spender All Time', topSpenders.all)}
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="user-filter-section">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className="px-4 py-1.5 rounded-full text-xs font-medium transition"
            style={{
              background: filter === f.id ? '#D4AF37' : 'rgba(255,255,255,0.05)',
              color: filter === f.id ? '#080C0A' : '#F8FAF9',
              border: `1px solid ${filter === f.id ? '#D4AF37' : 'rgba(255,255,255,0.1)'}`,
            }}
            data-testid={`filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="rounded-2xl overflow-hidden backdrop-blur-xl" style={cardStyle}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: 'rgba(0,0,0,0.3)' }}>
              <tr style={{ color: '#A3B8AC' }}>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Tier</th>
                <th className="text-right px-4 py-3">Lifetime Spending</th>
                <th className="text-right px-4 py-3">Bulan Ini</th>
                <th className="text-right px-4 py-3">Orders</th>
                <th className="text-right px-4 py-3">Kuji Draws</th>
                <th className="text-right px-4 py-3">Coins</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)', color: '#F8FAF9' }} data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      {u.name}
                      {u.role === 'admin' && <span className="text-[10px] px-1.5 rounded" style={{ background: 'rgba(212,175,55,0.2)', color: '#D4AF37' }}>ADM</span>}
                      {!u.email_verified && <ShieldAlert className="w-3.5 h-3.5" style={{ color: '#F59E0B' }} aria-label="Email belum diverifikasi" />}
                    </div>
                  </td>
                  <td className="px-4 py-3" style={{ color: '#A3B8AC' }}>{u.email}</td>
                  <td className="px-4 py-3">
                    {u.tier ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                        style={{ background: u.tier.color, color: '#080C0A' }}
                        data-testid={`tier-badge-${u.id}`}
                      >{u.tier.name}</span>
                    ) : <span className="text-xs italic" style={{ color: '#6B8079' }}>—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold" style={{ color: '#22C55E' }} data-testid={`lifetime-${u.id}`}>{fmtIDR(u.lifetime_spending_idr || 0)}</td>
                  <td className="px-4 py-3 text-right" style={{ color: '#CFE0D5' }}>{fmtIDR(u.monthly_spending_idr || 0)}</td>
                  <td className="px-4 py-3 text-right">{u.total_orders || 0}</td>
                  <td className="px-4 py-3 text-right">{u.total_kuji_draws || 0}</td>
                  <td className="px-4 py-3 text-right font-semibold" style={{ color: '#D4AF37' }}>{formatCoins(u.coin_balance || 0)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end flex-wrap">
                      <button onClick={() => setBalanceModalUser(u)} className="px-3 py-1 rounded-full text-xs font-semibold" style={goldBtn} data-testid={`adjust-${u.id}`}>Balance</button>
                      {!u.email_verified && (
                        <button
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/admin/users/${u.id}/verify-email`, {}, { withCredentials: true });
                              toast.success('Email diverifikasi');
                              const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
                              setUsersList(data);
                            } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
                          }}
                          className="px-2 py-1 rounded-full text-xs"
                          style={{ background: 'rgba(34,197,94,0.15)', color: '#22C55E' }}
                          data-testid={`admin-verify-${u.id}`}
                        >Verify</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
